import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgentSyncLogsAPIService } from 'src/app/extensions/services/api/agentSyncLogs-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { LogListFilterComponent } from './log-list-filter.component/log-list-filter.component';
import { Subject, Subscription } from 'rxjs';
import { scrollTop } from 'src/app/extensions/utilities';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SHORT_DEBOUNCE_TIME } from 'src/app/extensions/constants/commonConstants';
import { isEqual } from 'lodash';

@Component({
  selector: 'app-log-list',
  templateUrl: './log-list.component.html',
  styleUrls: ['./log-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class LogListComponent implements OnInit, OnDestroy {
  public constructor(
    private _scrollService: ScrollService,
    private _logAPIService: AgentSyncLogsAPIService,
    private _ngbModal: NgbModal
  ) {}

  public logs: app.models.GetPagedAgentSyncLogModel[];
  public props: app.models.DynamicPropsDictionary;

  private pageIndex = 1;
  private maxIndex: number;

  public activeFilterCount = 0;

  public subscriptions: Subscription[] = [];

  public loading: boolean = false;

  public showBackToTop: boolean = false;
  public searchQuery$ = new Subject<string>();

  private pageFilter: app.models.GetPagedAgentSyncLogFilterModel;
  public initialFilterState: app.models.GetPagedAgentSyncLogFilterModel;

  ngOnInit(): void {
    this.pageFilter = {
      startDate: null,
      endDate: null,
      searchFieldText: '',
    };
    this.initialFilterState = structuredClone(this.pageFilter);
    this.loadData();

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);
    let searchSubscription = this.searchQuery$
      .pipe(debounceTime(SHORT_DEBOUNCE_TIME), distinctUntilChanged())
      .subscribe(res => {
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }
        this.pageFilter.searchFieldText = res;
        this.loadData();
      });
    this.subscriptions.push(searchSubscription);

    let subscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._logAPIService.getPagedAgentSyncLogs(this.pageIndex, this.pageFilter).subscribe(x => {
          this.logs = this.logs.concat(x.data.values);

          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadData() {
    this._logAPIService.getPagedAgentSyncLogs(this.pageIndex, this.pageFilter).subscribe(x => {
      this.logs = x.data.values;
      this.props = x.props;
      this.maxIndex = x.data.totalPages;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  public filterHandler() {
    let instance = this._ngbModal.open(LogListFilterComponent);
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;
    instance.closed.subscribe(result => {
      if (result !== false) {
        this.pageIndex = 1;
        this.activeFilterCount = result;
        this.initialFilterState = structuredClone(this.pageFilter);

        this._logAPIService.getPagedAgentSyncLogs(this.pageIndex, this.pageFilter).subscribe(x => {
          this.logs = x.data.values;
          this.maxIndex = x.data.totalPages;

          this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
        });
      }
    });
  }

  public resetFiltersHandler() {
    this.pageFilter.startDate = null;
    this.pageFilter.endDate = null;
    this.pageFilter.searchFieldText = '';
    this.activeFilterCount = 0;
    this.loadData();
  }

  scrollToTop(): void {
    scrollTop();
  }
}
